import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Check, 
  X, 
  Edit, 
  Eye,
  Package,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import ApiService from '../../services/api';

interface ExtractedProduct {
  id: string;
  image_url: string;
  coordinates: {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  };
  extracted_data: {
    product_name: string;
    brand: string;
    price_old: number;
    price_new: number;
    discount: number;
    quantity: string;
    category: string;
  };
  confidence: number;
  needs_review: boolean;
  status: 'pending' | 'approved' | 'rejected';
}

interface ReviewStepProps {
  products: ExtractedProduct[];
  onUpdate: (products: ExtractedProduct[]) => void;
  onNext: () => void;
  onPrevious: () => void;
  offerId: string;
}

const ReviewStep: React.FC<ReviewStepProps> = ({ 
  products = [], 
  onUpdate, 
  onNext, 
  onPrevious, 
  offerId 
}) => {
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [filter, setFilter] = useState<'all' | 'pending' | 'needs_review'>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [extractedProducts, setExtractedProducts] = useState<ExtractedProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  // جلب المنتجات المستخرجة من الـ API (المصدر الوحيد)
  useEffect(() => {
    const fetchExtractedProducts = async () => {
      setLoadingProducts(true);
      setError(null);
      try {
        console.log(`Fetching products for offer: ${offerId}`);
        const productsData = await ApiService.getOfferProducts(offerId);
        
        // التعامل مع هيكل البيانات الصحيح من API
        const products = productsData.products || productsData || [];
        console.log(`Fetched ${products.length} products from API`);
        
        // تنظيف البيانات لإزالة أي تكرار محتمل بطريقة أكثر دقة
        const productMap = new Map();
        const uniqueProducts = [];
        
        products.forEach(product => {
          const key = `${product.id}_${product.image_url}`;
          if (!productMap.has(key)) {
            productMap.set(key, product);
            uniqueProducts.push(product);
          }
        });
        
        if (uniqueProducts.length !== products.length) {
          console.warn(`🔧 Removed ${products.length - uniqueProducts.length} duplicate products from API response`);
          console.log('Original products:', products.length, 'Unique products:', uniqueProducts.length);
        }
        
        setExtractedProducts(uniqueProducts);
      } catch (err) {
        setError('حدث خطأ أثناء جلب المنتجات');
        console.error('Error fetching products:', err);
        setExtractedProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    };
    
    if (offerId) {
      fetchExtractedProducts();
    } else {
      setError('لم يتم توفير معرف العرض');
      setExtractedProducts([]);
      setLoadingProducts(false);
    }
  }, [offerId]);
  
  // استخدام المنتجات من API فقط (تجاهل المنتجات الممررة كـ props)
  const displayProducts = extractedProducts;

  const filteredProducts = displayProducts.filter(product => {
    switch (filter) {
      case 'pending':
        return product.status === 'pending';
      case 'needs_review':
        return product.needs_review || product.confidence < 0.8;
      default:
        return true;
    }
  });

  const handleProductAction = async (productId: string, action: 'approve' | 'reject' | 'edit') => {
    const updatedProducts = extractedProducts.map(product => {
      if (product.id === productId) {
        return {
          ...product,
          status: action === 'approve' ? 'approved' as const : 
                  action === 'reject' ? 'rejected' as const : 
                  product.status
        };
      }
      return product;
    });
    
    // تحديث الحالة المحلية
    setExtractedProducts(updatedProducts);
    
    // تحديث الـ props للتوافق مع الكود القديم
    onUpdate(updatedProducts);

    if (action === 'approve') {
      await handleApprove(updatedProducts);
    }
  };

  const handleBulkAction = async (action: 'approve' | 'reject') => {
    const updatedProducts = extractedProducts.map(product => {
      if (selectedProducts.includes(product.id)) {
        return {
          ...product,
          status: action === 'approve' ? 'approved' as const : 'rejected' as const
        };
      }
      return product;
    });
    
    // تحديث الحالة المحلية
    setExtractedProducts(updatedProducts);
    
    // تحديث الـ props للتوافق مع الكود القديم
    onUpdate(updatedProducts);
    setSelectedProducts([]);
    
    // Send bulk action to backend
    if (selectedProducts.length > 0) {
      await handleApprove(updatedProducts);
    }
  };

  const handleSelectProduct = (productId: string) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.9) return 'text-success-600';
    if (confidence >= 0.7) return 'text-warning-600';
    return 'text-error-600';
  };

  const approvedCount = displayProducts.filter(p => p.status === 'approved').length;
  const pendingCount = displayProducts.filter(p => p.status === 'pending').length;
  const rejectedCount = displayProducts.filter(p => p.status === 'rejected').length;

  const canProceed = pendingCount === 0;

  const handleApprove = async (approvedProducts: ExtractedProduct[]) => {
    setLoading(true);
    setError(null);
    try {
      // إرسال المنتجات المعتمدة للـ API مع offer_id
      await ApiService.updateOfferProducts(offerId, approvedProducts);
      onUpdate(approvedProducts);
      onNext();
    } catch (e) {
      setError('حدث خطأ أثناء تحديث المنتجات');
    } finally {
      setLoading(false);
    }
  };

  if (loadingProducts) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-8"
      >
        <div className="max-w-7xl mx-auto text-center">
          <div className="text-lg">جاري تحميل المنتجات...</div>
        </div>
      </motion.div>
    );
  }

  // إذا لم توجد منتجات وانتهى التحميل
  if (!loadingProducts && displayProducts.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-8"
      >
        <div className="max-w-7xl mx-auto text-center">
          <div className="text-lg text-gray-600 mb-4">
            {error ? error : 'لم يتم العثور على منتجات في هذا العرض'}
          </div>
          <p className="text-gray-500 mb-6">
            قد يكون هذا بسبب فشل في معالجة الصور أو عدم وجود منتجات قابلة للكشف
          </p>
          <div className="flex justify-between items-center">
            <button
              onClick={onPrevious}
              className="btn-outline"
            >
              العودة للمعالجة
            </button>
            <button
              onClick={onNext}
              className="btn-primary"
            >
              متابعة بدون منتجات
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-8"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            مراجعة المنتجات المستخرجة
          </h2>
          <p className="text-gray-600">
            راجع وتحقق من دقة البيانات المستخرجة قبل النشر
          </p>
        </div>

        {/* إحصائيات سريعة */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{displayProducts.length}</div>
            <div className="text-sm text-gray-600">إجمالي المنتجات</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-success-600">{approvedCount}</div>
            <div className="text-sm text-gray-600">مقبول</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-warning-600">{pendingCount}</div>
            <div className="text-sm text-gray-600">بانتظار المراجعة</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-error-600">{rejectedCount}</div>
            <div className="text-sm text-gray-600">مرفوض</div>
          </div>
        </div>

        {/* فلاتر وإجراءات جماعية */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center space-x-4 space-x-reverse">
              <div className="flex items-center space-x-2 space-x-reverse">
                <label className="text-sm font-medium text-gray-700">عرض:</label>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value as any)}
                  className="input w-auto"
                >
                  <option value="all">جميع المنتجات</option>
                  <option value="pending">بانتظار المراجعة</option>
                  <option value="needs_review">يحتاج مراجعة</option>
                </select>
              </div>
            </div>

            {selectedProducts.length > 0 && (
              <div className="flex items-center space-x-3 space-x-reverse">
                <span className="text-sm text-gray-600">
                  {selectedProducts.length} منتج محدد
                </span>
                <button
                  onClick={() => handleBulkAction('approve')}
                  className="btn btn-sm bg-success-600 text-white hover:bg-success-700"
                >
                  <Check className="w-4 h-4 ml-1" />
                  قبول الكل
                </button>
                <button
                  onClick={() => handleBulkAction('reject')}
                  className="btn btn-sm bg-error-600 text-white hover:bg-error-700"
                >
                  <X className="w-4 h-4 ml-1" />
                  رفض الكل
                </button>
              </div>
            )}
          </div>
        </div>

        {/* شبكة المنتجات */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
          {filteredProducts.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`bg-white rounded-lg border-2 p-4 transition-all duration-200 ${
                selectedProducts.includes(product.id) 
                  ? 'border-primary-500 shadow-lg' 
                  : product.status === 'approved'
                    ? 'border-success-200'
                    : product.status === 'rejected'
                      ? 'border-error-200'
                      : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <input
                  type="checkbox"
                  checked={selectedProducts.includes(product.id)}
                  onChange={() => handleSelectProduct(product.id)}
                  className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 border-gray-300"
                />
                <div className="flex items-center space-x-2 space-x-reverse">
                  {product.status === 'approved' && (
                    <CheckCircle className="w-5 h-5 text-success-500" />
                  )}
                  {product.status === 'rejected' && (
                    <X className="w-5 h-5 text-error-500" />
                  )}
                  {product.needs_review && (
                    <AlertCircle className="w-5 h-5 text-warning-500" />
                  )}
                </div>
              </div>

              {/* صورة المنتج */}
              <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden mb-3">
                <img
                  src={product.image_url}
                  alt={product.extracted_data.product_name}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* بيانات المنتج */}
              <div className="space-y-2">
                <h3 className="font-medium text-gray-900 line-clamp-2 text-sm">
                  {product.extracted_data.product_name}
                </h3>
                
                <div className="flex items-center justify-between text-xs text-gray-600">
                  <span>{product.extracted_data.brand}</span>
                  <span className="badge bg-gray-100 text-gray-600">
                    {product.extracted_data.category}
                  </span>
                </div>

                {/* الأسعار */}
                <div className="flex items-center space-x-2 space-x-reverse">
                  <span className="text-sm font-bold text-gray-900">
                    {product.extracted_data.price_new} ر.س
                  </span>
                  <span className="text-xs line-through text-gray-500">
                    {product.extracted_data.price_old} ر.س
                  </span>
                  <span className="badge-success text-xs">
                    -{product.extracted_data.discount}%
                  </span>
                </div>

                {/* الكمية والثقة */}
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">{product.extracted_data.quantity}</span>
                  <span className={getConfidenceColor(product.confidence)}>
                    ثقة: {Math.round(product.confidence * 100)}%
                  </span>
                </div>
              </div>

              {/* أزرار الإجراءات */}
              <div className="flex items-center justify-between pt-3 border-t border-gray-200 mt-3">
                <button
                  onClick={() => handleProductAction(product.id, 'edit')}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <Edit className="w-4 h-4 text-gray-500" />
                </button>
                <div className="flex items-center space-x-2 space-x-reverse">
                  <button
                    onClick={() => handleProductAction(product.id, 'approve')}
                    className={`btn btn-sm ${
                      product.status === 'approved' 
                        ? 'bg-success-600 text-white' 
                        : 'bg-success-100 text-success-700 hover:bg-success-200'
                    }`}
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleProductAction(product.id, 'reject')}
                    className={`btn btn-sm ${
                      product.status === 'rejected' 
                        ? 'bg-error-600 text-white' 
                        : 'bg-error-100 text-error-700 hover:bg-error-200'
                    }`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* رسالة فارغة */}
        {filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">لا توجد منتجات</h3>
            <p className="text-gray-600">
              لا توجد منتجات تطابق الفلتر المحدد
            </p>
          </div>
        )}

        {/* تحذير إذا كان هناك منتجات معلقة */}
        {pendingCount > 0 && (
          <div className="bg-warning-50 border border-warning-200 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-3 space-x-reverse">
              <AlertCircle className="w-5 h-5 text-warning-600" />
              <div>
                <h4 className="font-medium text-warning-900">
                  يوجد {pendingCount} منتج بانتظار المراجعة
                </h4>
                <p className="text-sm text-warning-700">
                  يجب مراجعة جميع المنتجات قبل المتابعة للخطوة التالية
                </p>
              </div>
            </div>
          </div>
        )}

        {/* أزرار التنقل */}
        <div className="flex justify-between items-center pt-6">
          <button
            onClick={onPrevious}
            className="btn-outline"
          >
            السابق
          </button>
          
          <div className="text-sm text-gray-500">
            {approvedCount} من {displayProducts.length} منتج مقبول
          </div>
          
          <button
            onClick={() => handleApprove(extractedProducts)}
            disabled={loading}
            className={`btn-primary ${!canProceed || loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {loading ? 'جاري الحفظ...' : 'التالي: المراجعة النهائية'}
          </button>
          {error && <div className="text-red-600 mt-2">{error}</div>}
        </div>
      </div>
    </motion.div>
  );
};

export default ReviewStep;